/**
 * Pie and donut geometry.
 *
 * Angles are radians measured clockwise from 12 o'clock, which is where a pie chart's
 * first slice conventionally starts.
 */

import { type SortOrder, sortIndices } from "@/lib/tool-kit/charts/bars";

export interface PieItem {
  label: string;
  value: number;
}

export interface PieSlice {
  label: string;
  value: number;
  /** Fraction of the whole, 0–1. */
  share: number;
  startAngle: number;
  endAngle: number;
  /** True for the slice that collects the small categories. */
  other: boolean;
}

export interface PieOptions {
  sort?: SortOrder;
  /**
   * Merge categories smaller than this share (0–1) into one "Other" slice. Only
   * applied when at least two categories qualify: an "Other" holding one category just
   * hides its name.
   */
  otherBelow?: number | null;
  /** The "Other" slice's label. */
  otherLabel: string;
  /** Most slices drawn; the smallest beyond it are merged into "Other". */
  maxSlices?: number;
}

export interface PieLayout {
  slices: PieSlice[];
  /** Sum of the positive values — the whole the shares are taken of. */
  total: number;
  /** Zero and negative values, which cannot be a slice of anything. */
  dropped: PieItem[];
  /** The categories merged into "Other". */
  grouped: PieItem[];
}

function mergeSmallest(
  kept: PieItem[],
  grouped: PieItem[],
  total: number,
  { otherBelow = null, maxSlices = 8 }: PieOptions
): void {
  if (otherBelow !== null && otherBelow > 0) {
    const small = kept.filter((item) => item.value / total < otherBelow);
    if (small.length >= 2) {
      grouped.push(...small);
    }
  }
  const remaining = () => kept.filter((item) => !grouped.includes(item));
  // With an "Other" slice in play, it takes one of the `maxSlices` places.
  while (
    remaining().length + (grouped.length > 0 ? 1 : 0) > maxSlices &&
    remaining().length > 1
  ) {
    const rest = remaining();
    let smallest = rest[0];
    for (const item of rest) {
      if (item.value < smallest.value) {
        smallest = item;
      }
    }
    grouped.push(smallest);
  }
}

export function layoutPie(
  items: readonly PieItem[],
  options: PieOptions
): PieLayout {
  const kept = items.filter((item) => item.value > 0);
  const dropped = items.filter((item) => !(item.value > 0));
  const total = kept.reduce((sum, item) => sum + item.value, 0);
  const grouped: PieItem[] = [];
  if (total > 0) {
    mergeSmallest(kept, grouped, total, options);
    // Report the merged categories in the order they were entered.
    grouped.sort((a, b) => items.indexOf(a) - items.indexOf(b));
  }
  const visible = kept.filter((item) => !grouped.includes(item));
  const order = sortIndices(
    visible.map((item) => item.value),
    options.sort ?? "none"
  );
  const parts: { label: string; value: number; other: boolean }[] = order.map(
    (index) => ({ ...visible[index], other: false })
  );
  if (grouped.length > 0) {
    parts.push({
      label: options.otherLabel,
      value: grouped.reduce((sum, item) => sum + item.value, 0),
      other: true,
    });
  }
  let angle = 0;
  const slices = parts.map((part) => {
    const share = part.value / total;
    const slice: PieSlice = {
      ...part,
      share,
      startAngle: angle,
      endAngle: angle + share * 2 * Math.PI,
    };
    angle = slice.endAngle;
    return slice;
  });
  return { slices, total, dropped, grouped };
}

/** The point at `angle` (clockwise from 12 o'clock) on a circle. */
export function polar(
  cx: number,
  cy: number,
  radius: number,
  angle: number
): [number, number] {
  return [cx + radius * Math.sin(angle), cy - radius * Math.cos(angle)];
}

function point([x, y]: [number, number]): string {
  return `${x.toFixed(2)} ${y.toFixed(2)}`;
}

/**
 * An SVG path for one slice. `inner` > 0 draws a donut segment. A slice covering the
 * whole circle is drawn as two half arcs, since one arc from a point back to itself
 * draws nothing.
 */
export function arcPath(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  startAngle: number,
  endAngle: number
): string {
  const sweep = endAngle - startAngle;
  if (sweep >= 2 * Math.PI - 1e-9) {
    const top = polar(cx, cy, outer, 0);
    const bottom = polar(cx, cy, outer, Math.PI);
    const ring = `M ${point(top)} A ${outer} ${outer} 0 1 1 ${point(bottom)} A ${outer} ${outer} 0 1 1 ${point(top)} Z`;
    if (inner <= 0) {
      return ring;
    }
    const innerTop = polar(cx, cy, inner, 0);
    const innerBottom = polar(cx, cy, inner, Math.PI);
    return `${ring} M ${point(innerTop)} A ${inner} ${inner} 0 1 0 ${point(innerBottom)} A ${inner} ${inner} 0 1 0 ${point(innerTop)} Z`;
  }
  const large = sweep > Math.PI ? 1 : 0;
  const outerStart = polar(cx, cy, outer, startAngle);
  const outerEnd = polar(cx, cy, outer, endAngle);
  const arc = `M ${point(outerStart)} A ${outer} ${outer} 0 ${large} 1 ${point(outerEnd)}`;
  if (inner <= 0) {
    return `${arc} L ${point([cx, cy])} Z`;
  }
  const innerEnd = polar(cx, cy, inner, endAngle);
  const innerStart = polar(cx, cy, inner, startAngle);
  return `${arc} L ${point(innerEnd)} A ${inner} ${inner} 0 ${large} 0 ${point(innerStart)} Z`;
}

export interface SliceLabelPosition {
  /** Index into the slices. */
  index: number;
  x: number;
  y: number;
  anchor: "start" | "middle" | "end";
  /** Inside the slice (needs contrasting ink) or just outside the rim. */
  inside: boolean;
  /** For an outside label, the point on the rim its leader line starts from. */
  rim: [number, number] | null;
}

/** How far outside the rim an outside label sits. */
const LABEL_OFFSET = 16;

/**
 * Put a label pushed down its side's column back against the circle, so it never
 * slides across the pie: x is wherever the label's y meets a circle just outside it.
 */
function hugCircle(
  label: SliceLabelPosition,
  cx: number,
  cy: number,
  radius: number
): void {
  const dy = label.y - cy;
  const reach = Math.sqrt(Math.max(0, radius * radius - dy * dy));
  label.x = label.anchor === "start" ? cx + reach : cx - reach;
}

/**
 * Where each slice's label goes. A slice wide enough for its label gets it inside, at
 * the middle of the ring; a narrow one gets it outside the rim on its own side of the
 * circle, with a leader line back to the slice. Outside labels on one side are pushed
 * apart so they never overprint — the usual failure of a pie with several small slices
 * — and kept against the circle as they move, so they never slide across the pie.
 */
export function sliceLabelPositions(
  slices: readonly PieSlice[],
  {
    cx,
    cy,
    outer,
    inner,
    lineHeight,
    fits,
  }: {
    cx: number;
    cy: number;
    outer: number;
    inner: number;
    lineHeight: number;
    /** Whether slice `index`'s label fits inside it, given its arc length. */
    fits: (index: number, arcLength: number) => boolean;
  }
): SliceLabelPosition[] {
  const middle = inner > 0 ? (outer + inner) / 2 : outer * 0.62;
  const radius = outer + LABEL_OFFSET;
  const placed: SliceLabelPosition[] = [];
  const outside: SliceLabelPosition[] = [];
  for (const [index, slice] of slices.entries()) {
    const angle = (slice.startAngle + slice.endAngle) / 2;
    const span = slice.endAngle - slice.startAngle;
    if (fits(index, span * middle)) {
      const [x, y] = polar(cx, cy, middle, angle);
      placed.push({ index, x, y, anchor: "middle", inside: true, rim: null });
      continue;
    }
    const [x, y] = polar(cx, cy, radius, angle);
    outside.push({
      index,
      x,
      y,
      anchor: Math.sin(angle) >= 0 ? "start" : "end",
      inside: false,
      rim: polar(cx, cy, outer, angle),
    });
  }
  for (const side of ["start", "end"] as const) {
    const column = outside
      .filter((label) => label.anchor === side)
      .sort((a, b) => a.y - b.y);
    for (let i = 1; i < column.length; i++) {
      column[i].y = Math.max(column[i].y, column[i - 1].y + lineHeight);
    }
    for (const label of column) {
      hugCircle(label, cx, cy, radius);
    }
    placed.push(...column);
  }
  return placed.sort((a, b) => a.index - b.index);
}
